import { useEffect, useRef, useState } from "react"
import { useSearch, useLocation } from "wouter"
import { PageHeader } from "@/components/ui/PageHeader"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { EmptyState, Icon, KpiCard, KpiGrid } from "@/components/ds"
import { api } from "@/lib/api"
import { toast } from "sonner"
import type { DashboardData, MovimientoReciente } from "@/types/dashboard"
import { ConfiguracionInicialCard } from "@/components/onboarding/ConfiguracionInicialCard"
import { useSidebarBadgesContext } from "@/hooks/useSidebarBadges"
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia"
import { apiCapacitacionesDocumentosAdapter } from "@/lib/capacitaciones-documentos-adapter"
import {
  resumirDocumentosEmpresa,
  type ResumenDocumentosEmpresa,
} from "@/lib/documentos-empresa-resumen"
import type { AsignacionesKpis } from "@/types/capacitaciones-ia"
import { useTour } from "@/hooks/useTour"
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext"
import type { DriveStep } from "driver.js"

// Rutas de destino. Cada KPI y cada fila de "Requiere tu atención" lleva a la
// pantalla donde ESE número se resuelve — un dashboard que sólo informa
// obliga a buscar a mano dónde se arregla lo que acaba de señalar.
// `capacitaciones` y `avance` apuntaban a pantallas propias, retiradas por
// la fusión (WU4, sdd/capacitaciones-pantalla-unica): ambas repuntan al
// listado único, con el filtro de temática que reproduce lo que mostraban
// ("Enviadas" cubre tanto "recién enviada" como "atrasada" — sin plazo no
// hay una distinción más fina que hacer desde acá).
const RUTA = {
  documentos: "/configuracion/capacitaciones",
  capacitaciones: "/capacitaciones/programa?estado=enviada",
  avance: "/capacitaciones/programa?estado=enviada",
  programas: "/capacitaciones/programa",
  bandeja: "/documentos/bandeja-entrada",
} as const

// El tour del dashboard NO describe los widgets de esta pantalla. Recorre los
// menús del sidebar en el orden en que conviene configurarlos y termina en el
// panel de progreso. Los anclas `menu-*` viven en AppLayout (SidebarMenuItem);
// el detalle de cada módulo lo cubre su propia guía.
//
// No es un espejo del checklist de primeros pasos: Gestión Comercial está acá
// porque es donde se dan de alta mandantes y contratistas, aunque trabajar bajo
// un mandante no sea un paso que toda empresa tenga que completar.
const TOUR_STEPS: DriveStep[] = [
  {
    element: '[data-sidebar="sidebar"]',
    popover: {
      title: 'Navegación',
      description:
        'Desde esta barra lateral llegas a cada sección para configurar tu empresa. Te muestro los menús que corresponden a tus primeros pasos.',
    },
  },
  {
    element: '[data-tour="menu-empresa"]',
    popover: {
      title: 'Mi Empresa',
      description:
        'Empieza aquí: completa los datos de tu empresa y carga sus documentos de seguridad.',
    },
  },
  {
    element: '[data-tour="menu-estructura"]',
    popover: {
      title: 'Estructura',
      description:
        'Define las áreas y los cargos de tu organización. Los usarás al registrar trabajadores y contratos.',
    },
  },
  {
    element: '[data-tour="menu-trabajadores"]',
    popover: {
      title: 'Trabajadores',
      description:
        'Registra a tu equipo. Cada trabajador se asocia a un cargo y a un área.',
    },
  },
  {
    element: '[data-tour="menu-comercial"]',
    popover: {
      title: 'Gestión Comercial',
      description:
        'Da de alta a tus mandantes y contratistas, y crea los contratos que los vinculan.',
    },
  },
  {
    element: '[data-tour="menu-recursos"]',
    popover: {
      title: 'Recursos',
      description:
        'Carga los EPP, vehículos y maquinaria que usarás en los documentos firmados en terreno.',
    },
  },
  {
    element: '[data-tour="primeros-pasos"]',
    popover: {
      title: 'Primeros pasos',
      description:
        'Sigue tu avance aquí. Cada paso tiene su propia guía que te acompaña dentro de la sección.',
    },
  },
]

function tiempoRelativo(dateStr: string | null): string {
  if (!dateStr) return '—'
  const t = new Date(dateStr).getTime()
  if (Number.isNaN(t)) return '—'
  const hours = Math.floor((Date.now() - t) / 3_600_000)
  if (hours < 1) return 'recién'
  if (hours < 24) return `hace ${hours} h`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'ayer'
  return `hace ${days} días`
}

/** Glifo por tipo de documento firmado. Se empareja contra el valor crudo del
 * spine, nunca contra `tipo_recurso` (que es texto de presentación). */
const ICONO_TIPO: Record<string, string> = {
  EPP: 'shield',
  CAPACITACION: 'school',
  ATS: 'fact_check',
  CHECK_CAMIONETA: 'local_shipping',
  CHECK_MAQUINARIA: 'precision_manufacturing',
}

interface FilaAtencion {
  id: string
  icon: string
  /** Clases de tono del cuadrito del ícono (fondo + color). */
  tono: string
  titulo: string
  meta: string
  cta: string
  ruta: string
}

/**
 * "Requiere tu atención" — se arma plegando los MISMOS agregados que alimentan
 * los KPIs de arriba y los badges del sidebar, en orden de urgencia
 * (trabajadores sin responder → vencidos → por vencer → sin subir → IA en curso).
 * Las filas en cero no se muestran: una lista que siempre trae los mismos
 * cinco renglones deja de leerse.
 */
function construirAtencion(
  docs: ResumenDocumentosEmpresa | null,
  personasConDeuda: number,
  programasGenerando: number,
): FilaAtencion[] {
  const filas: FilaAtencion[] = []
  const plural = (n: number, s: string, p: string) => (n === 1 ? s : p)

  if (personasConDeuda > 0) {
    filas.push({
      id: 'sin-responder',
      icon: 'warning',
      tono: 'bg-destructive-bg text-destructive',
      titulo: `${personasConDeuda} ${plural(personasConDeuda, 'trabajador no ha respondido', 'trabajadores no han respondido')} su capacitación`,
      meta: 'Puedes revisar el detalle en el programa de capacitaciones',
      cta: 'Ver pendientes',
      ruta: RUTA.avance,
    })
  }
  if (docs && docs.vencidos > 0) {
    filas.push({
      id: 'vencidos',
      icon: 'error',
      tono: 'bg-destructive-bg text-destructive',
      titulo: `${docs.vencidos} ${plural(docs.vencidos, 'documento vencido', 'documentos vencidos')}`,
      meta: 'Dejan de servir como evidencia hasta renovarlos',
      cta: 'Renovar',
      ruta: RUTA.documentos,
    })
  }
  if (docs && docs.porVencer > 0) {
    filas.push({
      id: 'porvencer',
      icon: 'schedule',
      tono: 'bg-warning-bg text-warning',
      titulo: `${docs.porVencer} ${plural(docs.porVencer, 'documento por vencer', 'documentos por vencer')}`,
      meta: 'Con aviso programado al correo de la empresa',
      cta: 'Revisar',
      ruta: RUTA.documentos,
    })
  }
  if (docs && docs.faltantes > 0) {
    filas.push({
      id: 'faltantes',
      icon: 'folder_open',
      tono: 'bg-muted text-muted-foreground',
      // NO decir "exigibles": el catálogo es una lista de referencia, no una
      // obligación — qué documentos corresponden depende del tamaño y el rubro
      // de cada empresa. Presentarlos como requeridos hace que 24 pendientes se
      // lean como 24 incumplimientos, y el bloque pasa a ser ruido que se
      // ignora. La bajada apunta a la válvula de escape ("No aplica") en vez de
      // insistir con el pendiente.
      titulo: `${docs.faltantes} ${plural(docs.faltantes, 'documento del checklist sin subir', 'documentos del checklist sin subir')}`,
      // La acción va PRIMERO: la bajada es de una línea con elipsis, y en la
      // columna angosta lo que sobrevive tiene que ser la salida ("No aplica"),
      // no la procedencia del catálogo.
      meta: 'Marca «No aplica» los que no te correspondan · referencia DS 76',
      cta: 'Subir',
      ruta: RUTA.documentos,
    })
  }
  if (programasGenerando > 0) {
    filas.push({
      id: 'generando',
      icon: 'autorenew',
      tono: 'bg-primary-100 text-primary-hover',
      titulo: `${programasGenerando} ${plural(programasGenerando, 'programa generando temáticas', 'programas generando temáticas')} con IA`,
      meta: 'Podrás revisar y editar antes de enviar',
      cta: 'Ver programa',
      ruta: RUTA.programas,
    })
  }
  return filas
}

// TODO test (toast bienvenida): dado query param wizard=completado en la URL,
//   al montar el Dashboard se muestra un toast de éxito con mensaje de bienvenida,
//   y el query param se limpia de la URL (replace state) para no repetirse al refrescar.
// TODO test (sin query param): sin wizard=completado, no aparece toast.

export function Dashboard() {
  const [movimientos, setMovimientos] = useState<MovimientoReciente[] | null>(null)
  const [docs, setDocs] = useState<ResumenDocumentosEmpresa | null>(null)
  const [kpis, setKpis] = useState<AsignacionesKpis | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const search = useSearch()
  const [, navigate] = useLocation()
  const toastFiredRef = useRef(false)

  // Los mismos badges que pinta el sidebar — no un segundo request: si el KPI
  // "Con respuestas pendientes" y su badge salieran de snapshots distintos
  // podrían contradecirse en pantalla (spec §6, regla 1).
  const badges = useSidebarBadgesContext()

  const { startTour } = useTour('dashboard', TOUR_STEPS)
  useRegisterPageGuide(startTour)

  // Toast de bienvenida: se dispara una sola vez si ?wizard=completado está en la URL.
  // Limpia el query param con replace-state para evitar mostrar el toast al refrescar.
  useEffect(() => {
    if (toastFiredRef.current) return
    const params = new URLSearchParams(search)
    if (params.get('wizard') === 'completado') {
      toastFiredRef.current = true
      toast.success('¡Bienvenido! Tu cuenta está configurada.')
      // Limpiar el query param de la URL sin agregar entrada al historial
      navigate('/', { replace: true })
    }
  }, [search, navigate])

  // Cada bloque falla por separado: que no haya capacitaciones todavía no
  // puede dejar sin documentos al resto del tablero. Un solo toast si TODO
  // falló, para no apilar tres.
  useEffect(() => {
    let vivo = true
    Promise.allSettled([
      api.get<{ data: DashboardData }>('/dashboard'),
      apiCapacitacionesDocumentosAdapter.listSlots(),
      capacitacionesIaApi.getAsignacionesKpis(),
    ]).then(([resDashboard, resSlots, resKpis]) => {
      if (!vivo) return
      if (resDashboard.status === 'fulfilled') setMovimientos(resDashboard.value.data.ultimos_movimientos)
      if (resSlots.status === 'fulfilled') setDocs(resumirDocumentosEmpresa(resSlots.value))
      if (resKpis.status === 'fulfilled') setKpis(resKpis.value)
      if ([resDashboard, resSlots, resKpis].every((r) => r.status === 'rejected')) {
        toast.error('No se pudo cargar el dashboard')
      }
      setIsLoading(false)
    })
    return () => {
      vivo = false
    }
  }, [])

  const coberturaPct =
    kpis && kpis.denominador > 0 ? Math.round((kpis.completo / kpis.denominador) * 100) : null
  const personasConDeuda = badges?.personas_con_deuda ?? 0
  const conRespuestasPendientes = badges?.capacitaciones_con_respuestas_pendientes ?? 0
  const atencion = construirAtencion(docs, personasConDeuda, badges?.programas_generando ?? 0)

  return (
    <div className="flex flex-col gap-[18px]">
      <PageHeader
        title="Dashboard"
        subtitle="Qué requiere tu atención hoy y cómo va el cumplimiento de la empresa activa."
      />

      {/* Configuración inicial: a ancho completo y arriba de todo. Es lo
          primero que hay que resolver en una cuenta nueva; en una columna
          lateral de 320px competía con los widgets en vez de guiar. */}
      <div data-tour="primeros-pasos">
        <ConfiguracionInicialCard />
      </div>

      {isLoading ? (
        <KpiGrid>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[92px] rounded-xl" />
          ))}
        </KpiGrid>
      ) : (
        <KpiGrid>
          {/* "0/0" y "0 de 0" se leen como un dato medido cuando en realidad
              significan "todavía no hay nada". El guión lo dice sin fingir
              precisión. */}
          <KpiCard
            label="Documentos al día"
            icono="folder_open"
            valor={docs && docs.aplicables > 0 ? `${docs.cargados}/${docs.aplicables}` : '—'}
            detalle={
              !docs
                ? 'no se pudo leer el catálogo'
                : docs.aplicables === 0
                  ? 'el catálogo no tiene documentos'
                  : `${docs.faltantes} sin subir · ${docs.nNA} no aplican`
            }
            onClick={() => navigate(RUTA.documentos)}
          />
          <KpiCard
            label="Capacitaciones completadas"
            icono="school"
            valor={coberturaPct !== null ? `${coberturaPct}%` : '—'}
            detalle={
              kpis && kpis.denominador > 0
                ? `${kpis.completo} de ${kpis.denominador} cuestionarios enviados`
                : 'todavía no se envía ninguna capacitación'
            }
            onClick={() => navigate(RUTA.capacitaciones)}
          />
          <KpiCard
            label="Trabajadores sin responder"
            icono="warning"
            valor={String(personasConDeuda)}
            detalle="con capacitaciones sin cerrar"
            alerta={personasConDeuda > 0}
            onClick={() => navigate(RUTA.avance)}
          />
          <KpiCard
            label="Con respuestas pendientes"
            icono="send"
            valor={String(conRespuestasPendientes)}
            detalle="capacitaciones que aún reciben respuestas"
            onClick={() => navigate(RUTA.capacitaciones)}
          />
        </KpiGrid>
      )}

      <div
        className="grid items-start gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}
      >
        <PanelAtencion filas={atencion} isLoading={isLoading} onIr={navigate} />
        <PanelDocumentos
          movimientos={movimientos}
          isLoading={isLoading}
          onVerTodos={() => navigate(RUTA.bandeja)}
        />
      </div>
    </div>
  )
}

function PanelAtencion({
  filas,
  isLoading,
  onIr,
}: {
  filas: FilaAtencion[]
  isLoading: boolean
  onIr: (ruta: string) => void
}) {
  return (
    <section className="bg-card overflow-hidden rounded-xl border">
      <header className="border-border-subtle flex items-center gap-2.5 border-b px-4 py-3.5">
        <span className="fc-card-title flex-1">Requiere tu atención</span>
        {!isLoading && filas.length > 0 && (
          <span className="fc-num text-soft text-[11px]">
            {filas.length === 1 ? '1 asunto abierto' : `${filas.length} asuntos abiertos`}
          </span>
        )}
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-3 p-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[38px] rounded-lg" />
          ))}
        </div>
      ) : filas.length === 0 ? (
        <EmptyState
          icono="inbox"
          mensaje="Nada pendiente: documentos y capacitaciones están al día."
        />
      ) : (
        <ul>
          {filas.map((fila) => (
            <li
              key={fila.id}
              className="border-border-faint flex items-center gap-3 border-b px-4 py-[11px] last:border-b-0"
            >
              <span
                className={`flex size-[27px] shrink-0 items-center justify-center rounded-[7px] ${fila.tono}`}
              >
                <Icon name={fila.icon} size={15} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="fc-cell fc-truncate">{fila.titulo}</span>
                <span className="fc-meta fc-truncate">{fila.meta}</span>
              </div>
              <Button variant="outline" size="xs" onClick={() => onIr(fila.ruta)}>
                {fila.cta}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function PanelDocumentos({
  movimientos,
  isLoading,
  onVerTodos,
}: {
  movimientos: MovimientoReciente[] | null
  isLoading: boolean
  onVerTodos: () => void
}) {
  return (
    <section className="bg-card overflow-hidden rounded-xl border">
      <header className="border-border-subtle flex items-center gap-2.5 border-b px-4 py-3.5">
        <span className="fc-card-title flex-1">Últimos documentos sincronizados</span>
        <Button variant="ghost" size="xs" className="text-primary-hover" onClick={onVerTodos}>
          Ver todos
        </Button>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-3 p-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[38px] rounded-lg" />
          ))}
        </div>
      ) : !movimientos || movimientos.length === 0 ? (
        <EmptyState icono="cloud_off" mensaje="Todavía no llegan documentos firmados de terreno." />
      ) : (
        <ul>
          {movimientos.map((mov) => (
            <li
              key={mov.id}
              className="border-border-faint flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0"
            >
              <Icon
                name={ICONO_TIPO[mov.tipo] ?? 'description'}
                size={16}
                className="text-soft"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="fc-cell fc-truncate">{mov.tipo_recurso}</span>
                <span className="fc-meta fc-truncate">{mov.persona}</span>
              </div>
              <span className="fc-num text-meta shrink-0 text-[11px] whitespace-nowrap">
                {tiempoRelativo(mov.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
